
import { VNode } from 'vue';
import { VSelect, VTextField } from 'vuetify/lib';
import { getObjectValueByPath, keyCodes } from '@/utils/helpers';
import mergeVNodeData from '@/utils/mergeVNodeData';

interface Options extends InstanceType<typeof VSelect> {
  //    Inner component props
  placeholder: string;
  type: string;
  lazyValue: any;
  internalValue: any;
  appendIcon?: string;

  //    Computed data members
  isReadonly: boolean;
  isFocused: boolean;
  hasLabel: boolean;
  isDirty: boolean;
  persistentPlaceholder: boolean;
  isInteractive: boolean;
  isMenuActive: boolean;

  //    Methods
  onKeyPress(e: KeyboardEvent): void;
  isAppendInner(target: any): any;
  valueComparator: (a: any, b: any) => boolean;
  activateMenu(): void;
  //   Keyboard events
  onUpDown(e: KeyboardEvent): void;
  onEscDown(e: KeyboardEvent): void;
  onTabDown(e: KeyboardEvent): void;
  onSpaceDown(e: KeyboardEvent): void;

  genSlot(type: string, location: string, slot: (VNode | VNode[])[]): VNode;

  $refs: {
    menu: any; //cannot get VMenu instance type into this
    // menu: {
    //   listIndex: number;
    //   changeListIndex(e: KeyboardEvent): void;
    // };
    content: HTMLElement;
    label: HTMLElement;
    input: HTMLInputElement;
    'prepend-inner': HTMLElement;
    'append-inner': HTMLElement;
    prefix: HTMLElement;
    suffix: HTMLElement;
  };
}

export const defaultMenuProps = {
  closeOnClick: false,
  closeOnContentClick: false,
  disableKeys: true,
  openOnClick: false,
  maxHeight: 304,
  offsetY: true
};

export default VSelect.extend<Options>({
  functional: false
}).extend({
  name: 'TextAutocompleteField',

  props: {
    menuProps: {
      type: [String, Array, Object],
      default: () => defaultMenuProps
    }
  },

  methods: {
    genInput(): VNode {
      const input = (VTextField as any).options.methods.genInput.call(this);

      delete input.data!.attrs!.name;

      // Merging the node data of textField and this vue component
      input.data = mergeVNodeData(input.data!, {
        domProps: {
          value:
            this.type === 'number' && Object.is(this.internalValue, -0)
              ? '-0'
              : this.internalValue
        },
        attrs: {
          'aria-readonly': String(this.isReadonly),
          'aria-activedescendant': getObjectValueByPath(
            this.$refs.menu,
            'activeTile.id'
          ),
          autocomplete: getObjectValueByPath(
            input.data!,
            'attrs.autocomplete',
            'off'
          ),
          placeholder:
            !this.isDirty &&
            (this.persistentPlaceholder || this.isFocused || !this.hasLabel)
              ? this.placeholder
              : undefined
        }
        // on: { keypress: this.onKeyPress }
      });

      return input;
    },

    onKeyDown(e: KeyboardEvent) {
      if (this.isReadonly && e.code !== 'Tab') return;

      const keyCode = e.code;
      const menu = this.$refs.menu;

      this.$emit('keydown', e);

      if (!menu) return;

      // If menu is active, allow default
      // listIndex change from menu
      if (
        this.isMenuActive &&
        ['ArrowUp', 'ArrowDown', 'Home', 'End', 'Enter'].includes(keyCode)
      ) {
        this.$nextTick(() => {
          menu.changeListIndex(e);
          this.$emit('update:list-index', menu.listIndex);
        });
      }

      // If enter, open menu
      if (keyCode === 'Enter') this.activateMenu();

      // If menu is not active, up/down/home/end can do
      // one of 2 things. If multiple, opens the
      // menu, if not, will cycle through all
      // available options
      if (
        !this.isMenuActive &&
        ['ArrowUp', 'ArrowDown', 'Home', 'End'].includes(keyCode)
      )
        return this.onUpDown(e);

      // If escape deactivate the menu
      if (keyCode === 'Escape') return this.onEscDown(e);

      // If tab - select item or close menu
      if (keyCode === 'Tab') return this.onTabDown(e);
    },

    genCommaSelection(item: object, index: number, last: boolean) {
      return null;
    },

    genIconSlot() {
      const slot = [];

      if (this.$slots.append) {
        slot.push(this.$slots.append as VNode[]);
      }

      return this.genSlot('append', 'inner', slot);
    },

    setValue(value: any) {
      if (!this.valueComparator(value, this.internalValue)) {
        this.internalValue = value;
        this.$emit('change', value);
      }
    },

    onClick(e: MouseEvent) {
      if (!this.isInteractive) return;
      // new
      this.$refs.input.focus();

      if (!this.isAppendInner(e.target)) {
        this.isMenuActive = true;
      }

      if (!this.isFocused) {
        this.isFocused = true;
        this.$emit('focus');
      }

      this.$emit('click', e);
    }
  }
});
